/** Everything gameplay can read from input. Systems read actions, never keys or buttons. */
export type ActionId =
  | 'move'
  | 'look'
  | 'jump'
  | 'sprint'
  | 'pause'
  | 'interact'
  | 'skip'
  | 'useItem'
  | 'drop'
  | 'inventory'
  | 'phone'
  | 'toggleConsole'

/**
 * Actions that are held or tapped, as opposed to the `move` and `look` axes.
 * `toggleConsole` is the reserved Backquote key, not a binding or a HUD hint.
 */
export type ButtonActionId = Exclude<ActionId, 'move' | 'look' | 'toggleConsole'>

export type InputDevice = 'keyboardMouse' | 'gamepad' | 'touch'

/**
 * Menu navigation from a gamepad (and Escape as `back`), sent to the UI through `onUiIntent`.
 * `alternate` (X) and `quick` (Y) come only while the inventory screen is open.
 */
export type UiIntent = 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back' | 'alternate' | 'quick'

/** The buttons the on-screen touch controls can hold. */
export type TouchButtonAction =
  'jump' | 'sprint' | 'pause' | 'interact' | 'skip' | 'useItem' | 'drop'

/**
 * Physical keys that can't be bound: Escape is pause and back, Backquote is the
 * console. They still work in text fields.
 */
export const RESERVED_CODES: readonly string[] = ['Escape', 'Backquote']

/** Digit 1–9 choose a QuickAccess slot. Fixed like Escape, so they can't be bound either. */
export const QUICK_ACCESS_CODES: readonly string[] = Array.from(
  { length: 9 },
  (_, i) => `Digit${String(i + 1)}`,
)

/** Whether a key can't be bound: a reserved key or a QuickAccess digit. */
export function isUnbindable(code: string): boolean {
  return RESERVED_CODES.includes(code) || QUICK_ACCESS_CODES.includes(code)
}

/** What to show for an action's binding: a key (`E`, `Space`, `LMB`) and a gamepad button (`X`). */
export interface BindingLabel {
  readonly keyboard: string
  readonly gamepad: string
}

/** How the UI's touch controls feed the engine: one more input device. */
export interface VirtualInput {
  /** Joystick deflection: `x` right, `y` forward, each -1..1. Clamped to the unit circle. */
  setMove(x: number, y: number): void
  /** Look-drag distance in CSS pixels since the last call: `dx` right, `dy` down. */
  addLook(dxPx: number, dyPx: number): void
  setButton(action: TouchButtonAction, down: boolean): void
}
